import React from 'react';
import { Box, Paper, Typography, Chip, Alert, Tooltip } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, Cell, ReferenceLine, ZAxis,
  Tooltip as RTooltip, Label,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { Q_STYLE, fmt } from './constants';

const OppTooltip = ({ active, payload }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Paper sx={{ p: 1.5, maxWidth: 220, border: '1px solid #e2e8f0' }}>
      <Typography fontWeight={700} fontSize="0.85rem">{d.name_vi || d.name_en}</Typography>
      <Typography variant="caption" color="text.secondary" display="block">{d.name_en}</Typography>
      <Box sx={{ mt: 0.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px' }}>
        <Typography variant="caption">{t('icb.oppMap.tooltipRS')}:</Typography>
        <Typography variant="caption" fontWeight={600}>{fmt(d.rs_score)}</Typography>
        <Typography variant="caption">{t('icb.oppMap.tooltipFlow')}:</Typography>
        <Typography variant="caption" fontWeight={600}>{fmt(d.flow_score)}</Typography>
        <Typography variant="caption">{t('icb.oppMap.tooltipFinal')}:</Typography>
        <Typography variant="caption" fontWeight={600}>{fmt(d.final_score)}</Typography>
        <Typography variant="caption">{t('icb.oppMap.tooltipStockCount')}:</Typography>
        <Typography variant="caption" fontWeight={600}>{d.stock_count}</Typography>
      </Box>
      <Chip
        label={d.quadrant ? t(`icb.quadrant.${d.quadrant}`) : ''}
        size="small"
        sx={{ mt: 0.5, fontWeight: 600, fontSize: '0.65rem',
          color: Q_STYLE[d.quadrant]?.color, bgcolor: `${Q_STYLE[d.quadrant]?.color}18` }}
      />
    </Paper>
  );
};

const OpportunityMapTab = ({ scatterData, oppMap, expandedIcb, toggleLeaders }) => {
  const { t } = useTranslation();

  if (!scatterData.length) {
    return <Alert severity="info">{t('icb.common.noOpportunityData')}</Alert>;
  }

  return (
    <Box>
      {/* Legend */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #e2e8f0', display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mr: 1 }}>{t('icb.oppMap.legendTitle')}:</Typography>
        {Object.entries(Q_STYLE).map(([k, v]) => (
          <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: v.color }} />
            <Typography variant="caption" fontWeight={600} fontSize="0.75rem">{t(`icb.quadrant.${k}`)}</Typography>
          </Box>
        ))}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={t('icb.oppMap.axesHint')} arrow>
          <HelpOutline sx={{ fontSize: 18, color: '#94a3b8', cursor: 'help' }} />
        </Tooltip>
      </Paper>

      {/* Scatter chart */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <ResponsiveContainer width="100%" height={480}>
          <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis type="number" dataKey="rs_score" domain={[0, 100]} name="RS Score" tick={{ fontSize: 11 }} tickCount={6}>
              <Label value={t('icb.oppMap.xAxisLabel')} position="bottom" offset={0} style={{ fontSize: 12, fill: '#64748b', fontWeight: 600 }} />
            </XAxis>
            <YAxis type="number" dataKey="flow_score" domain={[0, 100]} name="Flow Score" tick={{ fontSize: 11 }} tickCount={6}>
              <Label value={t('icb.oppMap.yAxisLabel')} angle={-90} position="insideLeft" offset={10} style={{ fontSize: 12, fill: '#64748b', fontWeight: 600 }} />
            </YAxis>
            <ZAxis type="number" dataKey="stock_count" range={[80, 400]} />
            <RTooltip content={<OppTooltip />} cursor={{ strokeDasharray: '3 3' }} />

            <ReferenceLine x={55} stroke="#94a3b8" strokeDasharray="6 4" strokeWidth={1.5} />
            <ReferenceLine y={50} stroke="#94a3b8" strokeDasharray="6 4" strokeWidth={1.5} />

            <ReferenceLine x={27.5} y={75} ifOverflow="extendDomain" label={{ value: t('icb.quadrant.shortMONITOR'), fill: '#d9770640', fontSize: 14, fontWeight: 700 }} stroke="none" />
            <ReferenceLine x={77.5} y={75} ifOverflow="extendDomain" label={{ value: t('icb.quadrant.shortBUY'), fill: '#16a34a40', fontSize: 14, fontWeight: 700 }} stroke="none" />
            <ReferenceLine x={77.5} y={25} ifOverflow="extendDomain" label={{ value: t('icb.quadrant.shortEARLY_ENTRY'), fill: '#2563eb40', fontSize: 14, fontWeight: 700 }} stroke="none" />
            <ReferenceLine x={27.5} y={25} ifOverflow="extendDomain" label={{ value: t('icb.quadrant.shortAVOID'), fill: '#dc262640', fontSize: 14, fontWeight: 700 }} stroke="none" />

            <Scatter data={scatterData} nameKey="label">
              {scatterData.map((entry, idx) => (
                <Cell
                  key={idx}
                  fill={Q_STYLE[entry.quadrant]?.color || '#64748b'}
                  fillOpacity={0.75}
                  stroke={Q_STYLE[entry.quadrant]?.color || '#64748b'}
                  strokeWidth={1.5}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </Paper>

      {/* Quadrant summary below chart */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mt: 1.5, alignItems: 'stretch' }}>
        {Object.entries(oppMap?.quadrants || {}).map(([q, qItems]) => {
          const arr = Array.isArray(qItems) ? qItems : (qItems?.data || []);
          const st = Q_STYLE[q];
          if (!st) return null;
          return (
            <Box key={q} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', borderTop: `3px solid ${st.color}`, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
              <Typography fontWeight={700} fontSize="0.8rem" sx={{ color: st.color, mb: 1 }}>
                {t(`icb.quadrant.${q}`)} ({arr.length})
              </Typography>
              {arr.length === 0 ? (
                <Typography variant="caption" color="text.secondary">{t('icb.common.noGroupHere')}</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                  {arr.map((it) => {
                    const isOpen = expandedIcb === it.icb_code;
                    return (
                      <Box
                        key={it.icb_code}
                        onClick={() => toggleLeaders(it.icb_code)}
                        sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          cursor: 'pointer', px: 0.75, py: 0.4, borderRadius: 1,
                          bgcolor: isOpen ? `${st.color}1f` : 'transparent',
                          borderLeft: isOpen ? `2px solid ${st.color}` : '2px solid transparent',
                          transition: 'background-color 0.15s',
                          '&:hover': { bgcolor: isOpen ? `${st.color}2a` : `${st.color}12` },
                        }}
                      >
                        <Typography fontSize="0.8rem" fontWeight={500}>{it.name_vi || it.name_en}</Typography>
                        <Typography fontSize="0.75rem" color="text.secondary" sx={{ fontFeatureSettings: '"tnum"', ml: 1, flexShrink: 0 }}>
                          {fmt(it.final_score)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default OpportunityMapTab;
